// Shapes and wording for the Data health page (public.dashboard_data_health).
export type HealthAlert={level:'critical'|'warning';area:string;title:string;detail:string};
export type HealthSource={provider:string;last_at:string|null;last_24h:number;last_7d:number;prior_7d:number;unmatched_7d:number};
export type HealthConnection={provider:string;sync_kind:string;last_run_at:string|null;last_success_at:string|null;runs_24h:number;failures_24h:number;last_failure_reason:string|null;ever_succeeded:boolean};
export type UnlinkedLine={company:string;source:string|null;tracking_line:string|null;tracker_id:string|null;calls:number;calls_7d:number;last_call_at:string};
export type DeliveryFailure={created_at:string;lead_id:number|null;client:string;error_code:string|null;attempts:number};
/** A client lead that no text, email or call summary reached. */
export type UnreachedLead={received_at:string;lead_id:number;client:string;channel:'call'|'form';text_failed:boolean};
/** A client phone every recent text to has failed at the phone. Only its last four digits leave the database. */
export type FailingPhone={client:string|null;phone_last4:string;failures:number;first_failed_at:string;last_failed_at:string;last_error:string|null;client_has_email:boolean};
export type UnroutedSite={website:string;property_id:number|null;leads_7d:number;calls_7d?:number;forms_7d?:number;had_client:boolean;client_now?:boolean};
export type DataHealth={
 generated_at:string;alerts:HealthAlert[];sources:HealthSource[];connections:HealthConnection[];unmapped_lines:UnlinkedLine[];
 routing:{leads_7d:number;routed_7d:number;unrouted_7d:number;unrouted_from_past_client_sites_7d:number;unrouted_from_client_sites_7d?:number;unsold_7d?:number;unsold_sites_7d?:number;sites:UnroutedSite[]};
 delivery:{client_texts_7d:number;sent_7d:number;failed_7d:number;retries_exhausted_7d:number;other_failed_7d:number;waiting:number;waiting_over_1h:number;
  line_unavailable_24h?:number;client_leads_7d?:number;reached_7d?:number;unreached_7d?:number;unreached_48h?:number;text_failed_but_reached_7d?:number;
  unreached?:UnreachedLead[];phones_failing?:FailingPhone[]};
 delivery_failures:DeliveryFailure[];
 attribution:{leads_30d:number;with_campaign_source:number;with_google_click_id:number;with_meta_click_id:number;with_landing_page:number;direct:number;meta_leads:number;google_ads_leads:number};
 outcomes:{leads_with_outcome_30d:number;appointments_30d:number;revenue_30d:number;last_recorded_at:string|null};
 spend:{rows_30d:number;spend_30d:number;latest_date:string|null;platforms:string[]};
 transcripts:{enabled:boolean;completed_24h:number;waiting:number;failed_7d:number;minutes_today:number;daily_minute_cap:number|null;last_completed_at:string|null};
 tagging:{waiting:number;oldest_waiting_at:string|null;failed_7d:number};
 intake:{failed:number;waiting_over_15m:number;unmatched_7d:number};
};

const SOURCES:Record<string,string>={callrail:'CallRail calls',website:'Website forms',bland_ai:'Bland AI calls',fillout:'Fillout forms',tally:'Tally forms',
 airtable:'Airtable import',historical_ledger:'Historical ledger',master_sheet:'Master sheet import'};
const CONNECTIONS:Record<string,string>={callrail:'CallRail',google_ads:'Google Ads',meta_ads:'Meta Ads'};
export const sourceLabel=(provider:string)=>SOURCES[provider]||provider.replaceAll('_',' ');
export const connectionLabel=(c:{provider:string;sync_kind:string})=>`${CONNECTIONS[c.provider]||c.provider.replaceAll('_',' ')} ${c.sync_kind.replaceAll('_',' ')}`;

/** Plain-language reason for a delivery error code. */
export function deliveryError(code:string|null):string{
 if(!code)return 'Unknown error';
 const gaveUp=code.startsWith('retries_exhausted:'),base=gaveUp?code.slice(18):code;
 const reason=base.includes('provider_delivery_failed')||base==='comms_http_422'?'The carrier rejected the text'
  :base.includes('line_unavailable')?'The text line was unavailable'
  :base.includes('cellular_route_unavailable')?'No text route to this phone'
  :base==='send_lease_expired'?'The sender stopped responding'
  :base.replaceAll('_',' ');
 return gaveUp?`${reason}; gave up after 5 tries`:reason;
}

export const share=(part:number,whole:number)=>whole>0?Math.round(100*part/whole):0;

/** Why a website's leads had no client: it has one (the lead was outside its area or held), its client left, or it was never sold. */
export function siteStatus(site:UnroutedSite):{label:string;tone:'check'|'left'|'unsold'}{
 if(site.client_now)return {label:'Has a client: outside its area or held',tone:'check'};
 if(site.had_client)return {label:'Client left: sell or reassign',tone:'left'};
 return {label:'Never sold',tone:'unsold'};
}

/** "3 hours ago", "2 days ago"; exact dates for anything older than two weeks. */
export function ago(value:string|null,now=Date.now()):string{
 if(!value||Number.isNaN(Date.parse(value)))return 'Never';
 const minutes=Math.max(0,Math.round((now-Date.parse(value))/60_000));
 if(minutes<1)return 'Just now';
 if(minutes<60)return `${minutes} min ago`;
 const hours=Math.round(minutes/60);
 if(hours<48)return `${hours} hour${hours===1?'':'s'} ago`;
 const days=Math.round(hours/24);
 if(days<=14)return `${days} days ago`;
 return new Intl.DateTimeFormat('en-US',{month:'short',day:'numeric',year:'numeric'}).format(new Date(value));
}

/** Where to go to fix each kind of alert, when that page exists for this person. */
export const fixPage:Record<string,{page:string;label:string}>={
 delivery:{page:'lead-delivery',label:'Open Lead delivery & spam'},
 routing:{page:'routing',label:'Open Website routing'},
 mapping:{page:'phone-routing',label:'Open Phone routing'},
 intake:{page:'leads',label:'Open Leads'},
 outcomes:{page:'overview',label:'Record outcomes in Factory performance'},
};
const PLATFORMS:Record<string,string>={google_ads:'Google Ads',meta_ads:'Meta Ads',microsoft_ads:'Microsoft Ads'};
export const platformLabel=(p:string)=>PLATFORMS[p]||p.replaceAll('_',' ');
/** "Sep 25" from a YYYY-MM-DD date. */
export const shortDate=(d:string|null)=>d&&/^\d{4}-\d{2}-\d{2}$/.test(d)?new Intl.DateTimeFormat('en-US',{month:'short',day:'numeric',timeZone:'UTC'}).format(new Date(`${d}T00:00:00Z`)):'—';
